import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { List, Calendar as CalendarIcon, ArrowLeft, Check, CreditCard, PauseCircle, FileText } from "lucide-react";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { StatusBadge } from "../components/shared/StatusBadge";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { ErrorState } from "../components/shared/ErrorState";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { SubscriptionInfoCard } from "../components/subscriptions/SubscriptionInfoCard";
import { DeactivateDialog } from "../components/subscriptions/DeactivateDialog";
import { PauseResumeDialog } from "../components/subscriptions/PauseResumeDialog";
import { RenewDialog } from "../components/subscriptions/RenewDialog";
import { BonusTiffinDialog } from "../components/subscriptions/BonusTiffinDialog";
import { PaymentDialog } from "../components/payments/PaymentDialog";
import { ReceiptDialog } from "../components/payments/ReceiptDialog";
import { receiptFromPayment } from "../components/payments/PaymentReceipt";
import { GstBreakdown } from "../components/payments/GstBreakdown";
import { DeliveryCalendar } from "../components/deliveries/DeliveryCalendar";
import { DeliveryDetailDialog } from "../components/deliveries/DeliveryDetailDialog";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { formatDate, todayLocal } from "../lib/utils";
import { useTaxConfig, breakdownFromPayment } from "../lib/tax";
import { deliveryStatusMeta, paymentStatusMeta, paymentMethodLabel, paymentPurposeLabel } from "../lib/statusMaps";

function Detail({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p className="mt-1 text-sm font-medium text-text-main">{value || "—"}</p>
    </div>
  );
}

// Group a flat list of YYYY-MM-DD pause dates into contiguous [from,to] ranges.
function pauseRanges(dates) {
  const sorted = [...new Set(dates || [])].sort();
  const ranges = [];
  for (const d of sorted) {
    const last = ranges[ranges.length - 1];
    if (last) {
      const next = new Date(last.to + "T00:00:00");
      next.setDate(next.getDate() + 1);
      if (next.toISOString().slice(0, 10) === d) { last.to = d; continue; }
    }
    ranges.push({ from: d, to: d });
  }
  return ranges;
}

export default function SubscriptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState({ loading: true, error: null, sub: null });
  const [history, setHistory] = useState({ loading: true, deliveries: [], calendar: null });
  const [dialog, setDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [deliveryView, setDeliveryView] = useState("calendar");
  const [deliveryDetail, setDeliveryDetail] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const taxCfg = useTaxConfig();

  const load = useCallback(async () => {
    setState({ loading: true, error: null, sub: null });
    try {
      const { data } = await apiClient.get(`/subscriptions/${id}`);
      setState({ loading: false, error: null, sub: data });
    } catch (error) {
      setState({ loading: false, error: getApiErrorMessage(error, "Could not load this subscription."), sub: null });
    }
  }, [id]);

  const loadHistory = useCallback(async () => {
    setHistory((h) => ({ ...h, loading: true }));
    const [delivRes, calRes] = await Promise.allSettled([
      apiClient.get("/deliveries", { params: { subscription_id: id, limit: 100 } }),
      apiClient.get(`/deliveries/calendar/${id}`),
    ]);
    setHistory({
      loading: false,
      deliveries: delivRes.status === "fulfilled" ? delivRes.value.data.items || [] : [],
      calendar: calRes.status === "fulfilled" ? calRes.value.data : null,
    });
  }, [id]);

  useEffect(() => { load(); loadHistory(); }, [load, loadHistory]);

  // Any mutation on this page funnels through refreshAll; invalidate the cached
  // admin list + counts so the Subscriptions/Payments pages reflect the change.
  const refreshAll = () => {
    load();
    loadHistory();
    queryClient.invalidateQueries({ queryKey: ["subscriptions-list"] });
    queryClient.invalidateQueries({ queryKey: ["subscription-counts"] });
    queryClient.invalidateQueries({ queryKey: ["payments-list"] });
  };

  const handleReactivate = async () => {
    try {
      await apiClient.post(`/subscriptions/${id}/reactivate`);
      toast.success("Subscription reactivated");
      refreshAll();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not reactivate this subscription."));
    } finally {
      setDialog(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/subscriptions/${id}`);
      toast.success("Subscription deleted");
      queryClient.invalidateQueries({ queryKey: ["subscriptions-list"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-counts"] });
      navigate("/subscriptions");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not delete this subscription."));
    } finally {
      setDeleting(false);
      setDialog(null);
    }
  };

  const handleApprovePayment = async () => {
    setApproving(true);
    try {
      const { data } = await apiClient.post(`/payments/${state.sub.payment.id}/approve`);
      toast.success(data.new_subscription_id
        ? `Payment approved — renewal created (subscription #${data.new_subscription_id})`
        : "Payment approved — subscription activated");
      refreshAll();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not approve this payment."));
    } finally {
      setApproving(false);
    }
  };

  if (state.loading) {
    return (
      <div data-testid="subscription-detail-page">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-4 h-64 w-full" />
      </div>
    );
  }

  if (state.error || !state.sub) {
    return (
      <div data-testid="subscription-detail-page">
        <PageHeader
          title="Subscription"
          actions={<Button variant="secondary" onClick={() => navigate("/subscriptions")} data-testid="subscription-detail-back-button"><ArrowLeft size={16} strokeWidth={1.5} /> Back</Button>}
        />
        <ErrorState message={state.error || "Subscription not found."} onRetry={load} />
      </div>
    );
  }

  const s = state.sub;
  const isDeleted = s.status === 5;
  const _today = todayLocal();
  const _ranges = pauseRanges(history.calendar?.paused_dates);
  const activePause = _ranges.find((r) => r.from <= _today && _today <= r.to);
  const upcomingPause = !activePause
    ? _ranges.filter((r) => r.from > _today).sort((a, b) => a.from.localeCompare(b.from))[0]
    : null;

  return (
    <div data-testid="subscription-detail-page">
      <Button
        variant="secondary"
        onClick={() => navigate("/subscriptions")}
        className="mb-4 h-11 px-5 text-sm"
        data-testid="subscription-detail-back-button"
      >
        <ArrowLeft size={18} strokeWidth={1.75} /> Back
      </Button>

      <PageHeader
        title={<Link to={`/customers/${s.customer_id}`} className="hover:underline" data-testid="subscription-detail-customer-link">{s.customer_name || `Customer #${s.customer_id}`}</Link>}
        description={`Subscription #${s.id}`}
      />

      {/* SECTION 1 — Subscription Information (same layout in view & inline-edit) */}
      <SubscriptionInfoCard subscription={s} onSaved={refreshAll} onAction={setDialog} hasPause={!!(activePause || upcomingPause)} isCurrentlyPaused={!!activePause} />

      {/* SECTION 2 — Payments (all payment info + actions + history in one place) */}
      <Card className="mb-6" data-testid="subscription-payments-section">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
          <CardTitle>Payments</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline" size="sm"
              disabled={!s.payment || s.payment.status !== 2}
              title={s.payment && s.payment.status === 2 ? "" : "Available after payment"}
              onClick={() => setReceiptData(receiptFromPayment(s.payment, breakdownFromPayment(s.payment, taxCfg), s.customer_name))}
              data-testid="subscription-detail-receipt-button"
            ><FileText size={15} strokeWidth={1.5} /> Receipt</Button>
            <Button variant="outline" size="sm" onClick={() => setDialog("payment")} disabled={isDeleted || !s.payment} data-testid="subscription-detail-payment-button"><CreditCard size={15} strokeWidth={1.5} /> Manage Payment</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5 pt-4">
          {s.payment ? (
            <div className="grid gap-5 md:grid-cols-2">
              <div className="rounded-lg border border-border bg-background p-4" data-testid="subscription-detail-payment-amount">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Amount to Receive</p>
                <GstBreakdown bd={breakdownFromPayment(s.payment, taxCfg)} testId="subscription-detail-payment-breakdown" />
              </div>
              <div className="grid grid-cols-2 gap-4 self-start sm:grid-cols-3 md:grid-cols-2">
                <div data-testid="subscription-detail-payment-status">
                  <p className="text-xs uppercase tracking-wide text-text-muted">Status</p>
                  <div className="mt-1"><StatusBadge {...paymentStatusMeta(s.payment.status, s.payment.payment_method, s.payment.collected_at, s.payment.purpose, s.payment.checkout_state)} /></div>
                </div>
                <Detail label="Method" value={paymentMethodLabel(s.payment.payment_method)} testId="subscription-detail-payment-method" />
                <Detail label="Purpose" value={paymentPurposeLabel(s.payment.purpose)} testId="subscription-detail-payment-purpose" />
              </div>
            </div>
          ) : (
            <p className="text-sm text-text-muted">No payment on file for this subscription.</p>
          )}
          {s.payment && s.payment.status === 1 && s.payment.payment_method === "1" && (
            <Button variant="accent" size="sm" onClick={handleApprovePayment} disabled={approving} data-testid="subscription-detail-approve-payment-button">
              <Check size={15} strokeWidth={1.5} /> {approving ? "Approving..." : "Approve Cash Payment"}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* SECTION 3 — Deliveries */}
      <Card data-testid="subscription-deliveries-section">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
          <CardTitle>Deliveries</CardTitle>
          <div className="flex items-center gap-4">
            {history.calendar && (
              <div className="hidden gap-4 text-xs text-text-muted sm:flex" data-testid="subscription-calendar-summary">
                <span>Delivered: <strong className="text-text-main">{history.calendar.delivered_dates.length}</strong></span>
                <span>Not Delivered: <strong className="text-text-main">{history.calendar.failed_dates.length}</strong></span>
                <span>Skipped/Paused: <strong className="text-text-main">{history.calendar.skipped_dates.length}</strong></span>
              </div>
            )}
            <div className="flex rounded-md border border-border bg-surface p-1">
              <button type="button" onClick={() => setDeliveryView("list")} className={`rounded-sm p-1.5 ${deliveryView === "list" ? "bg-primary text-white" : "text-text-muted"}`} data-testid="subscription-deliveries-view-list">
                <List size={14} strokeWidth={1.5} />
              </button>
              <button type="button" onClick={() => setDeliveryView("calendar")} className={`rounded-sm p-1.5 ${deliveryView === "calendar" ? "bg-primary text-white" : "text-text-muted"}`} data-testid="subscription-deliveries-view-calendar">
                <CalendarIcon size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {(activePause || upcomingPause) && (
            <div
              className={`mb-4 flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${activePause ? "border-warning/30 bg-warning-bg text-warning" : "border-border bg-surface text-text-muted"}`}
              data-testid="subscription-pause-banner"
            >
              <PauseCircle size={16} strokeWidth={1.75} />
              <span className="font-medium">
                {activePause
                  ? `Paused now · ${formatDate(activePause.from)} → ${formatDate(activePause.to)}`
                  : `Pause scheduled · ${formatDate(upcomingPause.from)} → ${formatDate(upcomingPause.to)}`}
              </span>
            </div>
          )}
          {deliveryView === "calendar" ? (
            <DeliveryCalendar deliveries={history.deliveries} subscription={s} skippedDates={history.calendar?.skipped_dates} />
          ) : (
            <DataTable
              testId="subscription-deliveries-table"
              loading={history.loading}
              rows={history.deliveries}
              emptyTitle="No deliveries yet"
              columns={[
                { key: "id", header: "ID" },
                { key: "date", header: "Date", render: (r) => formatDate(r.date) },
                { key: "status", header: "Status", render: (r) => {
                  if (r.delivery_status === 2 && r.delivered_by_admin) {
                    return <StatusBadge label="Delivered by Admin" variant="success" data-testid={`subscription-delivery-${r.id}-admin`} />;
                  }
                  const m = deliveryStatusMeta(r.delivery_status); return <StatusBadge label={m.label} variant={m.variant} />;
                } },
                { key: "arrival", header: "Delivery Time", render: (r) => (r.delivery_status === 2 ? (r.arrival || "—") : "—") },
                { key: "pod", header: "Proof of Delivery", render: (r) => ((r.pof1 || r.pof2) ? (
                  <Button variant="outline" size="sm" onClick={() => setDeliveryDetail({ delivery: r, date: r.date })} data-testid={`subscription-delivery-${r.id}-pod-button`}>
                    View Photos
                  </Button>
                ) : "—") },
              ]}
            />
          )}
        </CardContent>
      </Card>

      <PauseResumeDialog
        open={dialog === "pause" || dialog === "resume"} onOpenChange={(v) => setDialog(v ? dialog : null)}
        mode={dialog === "resume" ? "resume" : "pause"}
        pauseUrl={`/subscriptions/${id}/pause`}
        resumeUrl={`/subscriptions/${id}/resume-pause`}
        batchUrl={`/subscriptions/${id}/pause-batch`}
        pauseMinDate={_today}
        onDone={() => { setDialog(null); refreshAll(); }}
      />
      <RenewDialog
        open={dialog === "renew"} onOpenChange={(v) => setDialog(v ? "renew" : null)}
        subscriptionId={id} onDone={() => { setDialog(null); refreshAll(); }}
      />
      <PaymentDialog
        open={dialog === "payment"} onOpenChange={(v) => setDialog(v ? "payment" : null)}
        payment={s.payment ? { ...s.payment, payment_link: s.payment_link } : null} onDone={refreshAll}
      />
      <ReceiptDialog open={!!receiptData} onOpenChange={(v) => !v && setReceiptData(null)} data={receiptData} />
      <DeliveryDetailDialog
        open={!!deliveryDetail} onOpenChange={(v) => !v && setDeliveryDetail(null)}
        delivery={deliveryDetail?.delivery} date={deliveryDetail?.date}
      />
      <DeactivateDialog
        open={dialog === "deactivate"} onOpenChange={(v) => setDialog(v ? "deactivate" : null)}
        subscriptionId={id} onDone={() => { setDialog(null); refreshAll(); }}
      />
      <ConfirmDialog
        open={dialog === "reactivate"} onOpenChange={(v) => setDialog(v ? "reactivate" : null)}
        title="Reactivate this subscription?" description="Deliveries will resume being scheduled for this customer."
        confirmLabel="Reactivate" variant="default" onConfirm={handleReactivate}
        testId="subscription-reactivate-confirm-dialog"
      />
      <BonusTiffinDialog
        open={dialog === "bonus"} onOpenChange={(v) => setDialog(v ? "bonus" : null)}
        subscription={s} onSaved={refreshAll}
      />
      <ConfirmDialog
        open={dialog === "delete"} onOpenChange={(v) => setDialog(v ? "delete" : null)}
        title="Delete this subscription?" description="This soft-deletes the subscription. It will no longer appear in the default list or be eligible for delivery generation."
        confirmLabel="Delete Subscription" variant="danger" loading={deleting} onConfirm={handleDelete}
        testId="subscription-delete-confirm-dialog"
      />
    </div>
  );
}
